/** Default topic list: featured for this topic first (Ratings order), then Popular (matches topic.jsx initial state). */
import { useState, useEffect, useCallback } from "react"
import { useLocation, navigate } from "@reach/router"

import { getParamsFromUrl, buildQueryString } from "./urls"
import { DEFAULT_TOPIC_SORT, TOPIC_SORT_OPTIONS } from "./topics"

const DEFAULT_COMMUNITY_SORT = "Popular"
const COMMUNITY_SORT_OPTIONS = [
  "Popular",
  "Ratings",
  "Largest",
  "Newest",
  "Growth",
]

const toStatNumber = v => {
  if (v == null || v === "") return 0
  const n = Number(String(v).replace(/,/g, ""))
  return Number.isFinite(n) ? n : 0
}

const communityViewsGrowthPercent = c => {
  const prev = toStatNumber(c.viewsPrev)
  if (prev <= 0) return null
  return Math.round(((toStatNumber(c.views) - prev) / prev) * 100)
}

const orderedCommunitiesForTopicPageDefault = (
  topicSlug,
  communitiesInTopic
) => {
  const list = communitiesInTopic.map(c => ({ ...c }))
  const promotedCommunities = list.filter(
    c =>
      c.promoted &&
      c.promotedTopics &&
      c.promotedTopics.split(",").includes(topicSlug)
  )
  let filteredCommunities = sortCommunities("Popular", [...list])
  filteredCommunities = [
    ...sortCommunities(
      "Ratings",
      promotedCommunities.map(c => ({ ...c, showPromotedTag: true }))
    ),
    ...filteredCommunities.filter(
      c => promotedCommunities.map(p => p.slug).indexOf(c.slug) === -1
    ),
  ]
  return filteredCommunities
}

/** 1-based rank on the default topic ordering, or null if not in the list. */
const getCommunityRankOnTopicPageDefault = (
  communitySlug,
  topicSlug,
  communitiesInTopic
) => {
  const ordered = orderedCommunitiesForTopicPageDefault(
    topicSlug,
    communitiesInTopic
  )
  const idx = ordered.findIndex(c => c.slug === communitySlug)
  return idx === -1 ? null : idx + 1
}

/** Matches topic.jsx when the visitor chooses "Largest": promoted block first, then everyone else by member count. */
const orderedCommunitiesForTopicPageLargest = (
  topicSlug,
  communitiesInTopic
) => {
  const list = communitiesInTopic.map(c => ({ ...c }))
  const promotedCommunities = list.filter(
    c =>
      c.promoted &&
      c.promotedTopics &&
      c.promotedTopics.split(",").includes(topicSlug)
  )
  let filteredCommunities = sortCommunities("Largest", [...list])
  filteredCommunities = [
    ...sortCommunities(
      "Ratings",
      promotedCommunities.map(c => ({ ...c, showPromotedTag: true }))
    ),
    ...filteredCommunities.filter(
      c => promotedCommunities.map(p => p.slug).indexOf(c.slug) === -1
    ),
  ]
  return filteredCommunities
}

/** 1-based rank on the topic page "Largest" ordering, or null if not in the list. */
const getCommunityRankOnTopicPageLargest = (
  communitySlug,
  topicSlug,
  communitiesInTopic
) => {
  const ordered = orderedCommunitiesForTopicPageLargest(
    topicSlug,
    communitiesInTopic
  )
  const idx = ordered.findIndex(c => c.slug === communitySlug)
  return idx === -1 ? null : idx + 1
}

const sortCommunities = (sort, filteredCommunities) => {
  // sort them
  if (sort === "Largest") {
    filteredCommunities = filteredCommunities.sort((a, b) =>
      a.members < b.members ? 1 : b.members < a.members ? -1 : 0
    )
  } else if (sort === "Popular") {
    // first sort by feature count (in case this is a new topic)
    filteredCommunities = filteredCommunities.sort((a, b) =>
      a.countFeatures < b.countFeatures
        ? 1
        : b.countFeatures < a.countFeatures
        ? -1
        : 0
    )

    // real sort
    filteredCommunities = filteredCommunities.sort((a, b) =>
      a.redirects < b.redirects ? 1 : b.redirects < a.redirects ? -1 : 0
    )
  } else if (sort === "Growth") {
    filteredCommunities = filteredCommunities.sort((a, b) => {
      const aGrowth = communityViewsGrowthPercent(a)
      const bGrowth = communityViewsGrowthPercent(b)
      if (aGrowth == null && bGrowth == null) return 0
      if (aGrowth == null) return 1
      if (bGrowth == null) return -1
      return aGrowth < bGrowth ? 1 : bGrowth < aGrowth ? -1 : 0
    })
  } else if (sort === "Ratings") {
    // sort first by count features, then by total number of stars (from reviews) if they have them
    filteredCommunities = filteredCommunities.sort((a, b) =>
      a.countFeatures < b.countFeatures
        ? 1
        : b.countFeatures < a.countFeatures
        ? -1
        : 0
    )
    filteredCommunities = filteredCommunities
      .map(c => Object.assign(c, { stars: c.reviewCount * c.reviewAvg }))
      .sort((a, b) => (a.stars < b.stars ? 1 : b.stars < a.stars ? -1 : 0))

    // low-rated communities go to bottom
    filteredCommunities = [
      ...filteredCommunities.filter(
        c => c.reviewCount === "0" || parseInt(c.reviewAvg) >= 3
      ),
      ...filteredCommunities.filter(
        c => c.reviewCount !== "0" && parseInt(c.reviewAvg) < 3
      ),
    ]
  } else if (sort === "Features") {
    // not used
    filteredCommunities = filteredCommunities.sort((a, b) =>
      a.countFeatures < b.countFeatures
        ? 1
        : b.countFeatures < a.countFeatures
        ? -1
        : 0
    )
  } else if (!sort || sort === "Newest") {
    filteredCommunities = filteredCommunities.sort((a, b) =>
      a.order < b.order ? 1 : b.order < a.order ? -1 : 0
    )
  } else if (!sort || sort === "Oldest") {
    filteredCommunities = filteredCommunities.sort((a, b) =>
      a.order < b.order ? -1 : b.order < a.order ? 1 : 0
    )
  }
  return filteredCommunities
}

function parseSortFromUrl(location, sortOptions, defaultSort) {
  const raw = getParamsFromUrl(location).sort
  if (raw && sortOptions.includes(raw)) return raw
  return defaultSort
}

function useSortUrl({ sortOptions, defaultSort }) {
  const location = useLocation()
  const [sort, setSortState] = useState(() =>
    parseSortFromUrl(location, sortOptions, defaultSort)
  )

  useEffect(() => {
    setSortState(parseSortFromUrl(location, sortOptions, defaultSort))
  }, [location.search, sortOptions, defaultSort, location])

  const setSort = useCallback(
    nextSort => {
      setSortState(nextSort)
      const params = getParamsFromUrl(location)
      if (nextSort === defaultSort) {
        delete params.sort
      } else {
        params.sort = nextSort
      }
      navigate(
        location.pathname + buildQueryString(params) + (location.hash || ""),
        { replace: true }
      )
    },
    [location, defaultSort]
  )

  return {
    sort,
    setSort,
    sortOptions,
  }
}

function useCommunityListSort() {
  return useSortUrl({
    sortOptions: COMMUNITY_SORT_OPTIONS,
    defaultSort: DEFAULT_COMMUNITY_SORT,
  })
}

function useTopicListSort() {
  return useSortUrl({
    sortOptions: TOPIC_SORT_OPTIONS,
    defaultSort: DEFAULT_TOPIC_SORT,
  })
}

export {
  DEFAULT_COMMUNITY_SORT,
  COMMUNITY_SORT_OPTIONS,
  sortCommunities,
  getCommunityRankOnTopicPageDefault,
  getCommunityRankOnTopicPageLargest,
  useSortUrl,
  useCommunityListSort,
  useTopicListSort,
}
